import theme from 'antd/es/theme';
import Typography from 'antd/es/typography';
import { type ReactNode, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const { Text } = Typography;

const ThinkingContainer = styled.div`
  margin: 8px 0;
`;

const ThoughtsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ThoughtRow = styled.div`
  display: flex;
  margin-bottom: 0;
`;

const IconContainer = styled.div`
  width: 16px;
  margin-top: 7px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
`;

const ThoughtIcon = styled.div`
  width: 8px;
  height: 8px;
  margin-top: 6px;
  flex-shrink: 0;
`;

const ThoughtDot = styled(ThoughtIcon)`
  border-radius: 50%;
  background-color: ${(props) => props.color || 'currentColor'};
`;

const ThoughtBubble = styled.div`
  border-radius: 8px;
  padding: 2px 12px;
  flex: 1;
`;

const ThoughtText = styled(Text)`
  margin: 0;
  line-height: 1.4;
`;

const pulse = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`;

const ThinkingDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.token.colorPrimary};
  animation: ${pulse} 1.5s ease-in-out infinite;
  margin-left: 4px;
  vertical-align: middle;
`;

export interface Thought {
  text: string;
  createdAt: Date;
}

export interface ThinkingProgressProps {
  thoughts?: Array<Thought>;
  isStreaming?: boolean;
  inverted?: boolean;
  progress?: number;
}

/**
 * A component that displays thinking progress
 */
const ThinkingProgress = ({
  thoughts,
  isStreaming,
  inverted,
}: ThinkingProgressProps): ReactNode => {
  const { token } = theme.useToken();
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset to collapsed state when streaming ends
  useEffect(() => {
    setIsExpanded(false);
  }, [isStreaming, thoughts]);

  // If streaming but no thoughts, show placeholder
  if (isStreaming && (!thoughts || thoughts.length === 0)) {
    return (
      <ThinkingContainer>
        <ThoughtsList>
          <ThoughtRow>
            <IconContainer>
              <ThinkingDot />
            </IconContainer>
            <ThoughtBubble style={inverted ? { color: '#fff' } : undefined}>
              <ThoughtText>Thinking...</ThoughtText>
            </ThoughtBubble>
          </ThoughtRow>
        </ThoughtsList>
      </ThinkingContainer>
    );
  }

  // If no thoughts, render nothing
  if (!thoughts || thoughts.length === 0) {
    return null;
  }

  // Most recent thought first
  const sortedThoughts = [...thoughts].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  );

  // Show only most recent thought when collapsed
  const visibleThoughts = isExpanded ? sortedThoughts : [sortedThoughts[0]];

  return (
    <ThinkingContainer>
      <ThoughtsList>
        {visibleThoughts.map((thought, index) => (
          <ThoughtRow key={thought.createdAt.getTime()}>
            <IconContainer>
              {index === 0 && isStreaming ? (
                <ThinkingDot />
              ) : index === 0 ? (
                <ThoughtDot color={token.colorPrimary} />
              ) : null}
            </IconContainer>
            <ThoughtBubble style={inverted ? { color: '#fff' } : undefined}>
              <ThoughtText>{thought.text}</ThoughtText>
            </ThoughtBubble>
          </ThoughtRow>
        ))}
      </ThoughtsList>
    </ThinkingContainer>
  );
};

export default ThinkingProgress;
